import axios from 'axios';
import { axiosInstance } from '../utils';

const getFormatAndExpiry = (files) => {
  let [format, expiry] = [files?.type?.split('/')[0], '86400'];
  if (files?.name?.toLowerCase()?.endsWith('.mov')) {
    format = 'video';
  }
  return [format, expiry];
};

export const getUploadId = async (url, format, mimeType, signal?, metadata?) => {
  const type = format === 'audio' ? 'Audio' : format === 'image' ? 'Image' : 'Video';
  const formData = {
    url: url,
    type,
    mimeType,
    ...(metadata ? { metadata } : {}),
  };

  try {
    const response = await axiosInstance.post('/api/uploads', formData, { signal });

    if (response.data) {
      return response.data;
    }
  } catch (error) {
    console.error(error);
  }
};

export const convertFileToBuffer = (file: File): Promise<{ buffer: Buffer }> => {
  return new Promise((resolve, reject) => {
    if (file) {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onloadend = () => {
        try {
          const buffer = Buffer.from(reader.result as ArrayBuffer);
          resolve({ buffer });
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
    } else {
      reject(new Error('No file provided'));
    }
  });
};

export const uploadFileToPresignedUrl = async (
  uploadId,
  presignedUrls,
  file,
  handleUploadProgress?: (percentCompleted: number) => void,
  signal?
) => {
  try {
    const presignedUrl = new URL(presignedUrls[0].url);
    const objectKey = presignedUrl.pathname.replace(/^\/+/, "").split('/').slice(1).join('/');

    const { buffer } = await convertFileToBuffer(file);

    const chunkSize = 50 * 1024 * 1024; // 50MB
    let partNumber = 1;
    const etags: { PartNumber: number; ETag: string }[] = [];
    const retries= 3;
    for (const { url } of presignedUrls) {
      const start = (partNumber - 1) * chunkSize;
      const end = Math.min(start + chunkSize, buffer.length);
      const chunk = buffer.slice(start, end);
      for(let attempt = 1; attempt <= retries; attempt++) {
        try {
          const response = await axiosInstance.put(url, chunk, {
            headers: {
              'Content-Type': file.mimetype,
              'skipAuth': 'true',
            },
            onUploadProgress: (progressEvent) => {
              const currentPartProgress = Math.round((progressEvent.loaded) / progressEvent.total);
              const overallProgress = Math.round(
                ((partNumber -1 + currentPartProgress) / presignedUrls.length) * 100
              );
              if(handleUploadProgress) handleUploadProgress(overallProgress);
            },
            signal,
          });

          const etag = response.headers['etag'] || '';
          etags.push({ PartNumber: partNumber, ETag: etag });
          partNumber++;
          break;
        } catch (error) {
          if (axios.isCancel?.(error) || error.code === "ERR_CANCELED" || error?.name === 'CanceledError') {
            console.warn(`Upload cancelled at part ${partNumber}`);
            throw error;
          }
          console.error(`Error uploading part ${partNumber}: ${error.message}`);
          if (attempt === retries) {
            console.error('Max retry attempts reached. Upload failed.');
            throw new Error(
              `Upload failed after ${retries} attempts for part ${partNumber}: ${error.message}`,
            );
          }
          await new Promise((resolve) => setTimeout(resolve, 5000 * attempt));
        }
      }
    }

    const formData = {
      objectKey,
      uploadId,
      parts: etags,
    };
    const response = await axiosInstance.post('/api/s3/complete-multi-part-upload', formData);

    if(response.data) {
      return response.data;
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const putPreSignedUrl = async (files) => {
  const [format] = getFormatAndExpiry(files);

  const numberOfParts = Math.ceil(files.size / (50 * 1024 * 1024)); // 50MB per part
  const mimeType = files.type
  const formData = {
    format,
    // expiry,
    numberOfParts,
    mimeType
  };

  try {
    const response = await axiosInstance.post('/api/s3/get-multi-presigned-urls', formData);

    if (response.data) {
      return { uploadId: response.data.uploadId, preSignedUrls: response.data.urls, format };
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};
